import React, { useCallback, useEffect, useState, useMemo, Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import InputMask from "react-input-mask";
import { useDispatch, useSelector } from "react-redux";
import { Flex, Header, Footer, SEO } from "./Components";
import { AppointmentModal } from "./Components/Modals/AppointmentModal";
import { SuccessAlert } from "./Components/Alerts";
import { ContactModal } from "./Components/Modals/ContactModal";
import { ParallaxProvider } from "react-scroll-parallax";
import { StaticAppointmentButton } from "./Components/Button/StaticAppointmentButton";
import { SocialMediaBlock } from "./Components/SocialMediaBlock";
import RetirementPlanning from "./Assets/Images/retirementPlanning.jpeg";
import EstatePlanning from "./Assets/Images/estatePlanning.jpg";
import {
  FinancialPlan,
  TeamImage,
  TeamImageSecondary,
  Office,
  Consultation,
  WaypointMonitoring,
  EagleParallax,
  Plane,
  TeamImagev3,
  Venue,
  PilotsCockpit,
  BusinessOffice,
  MedicalProfessional,
  RetirementSponsor,
} from "./Assets";
import {
  ExitStrategy,
  RiskIcon,
  SettingsChartIcon,
  ChartUpIcon,
  BarChart,
  Plan,
  MitigatingRisk,
  PlanningPilots,
  StockOptions,
  CheckList,
  Pilot,
  Business,
  Indivudual,
  Analytics,
  Roadmap,
  Team,
  Vision,
  Chart,
  Shield,
  Product,
  Timewheel,
  HandShakeImg,
  BusinessStructureImg,
} from "./Assets/Icons";
import { UPDATE_WINDOW_SIZE } from "./Redux/Constants";
import { WindowSizes } from "./Redux/Models/Utility/WIndowSizes";
import {
  GetBusinessServicesAction,
  GetIndividualServicesAction,
  GetMainMetaTagsAction,
} from "./Redux/Actions";
import { MobileDrawer } from "./Components";
import { PageSkeleton } from "./Components/PageSkeleton/PageSkeleton";
import { EventsPageSkeleton } from "./Components/EventsPage/SeminarSkeletons/SeminarSkeletons";
import ReactGA from "react-ga4";
import { useLocation } from "react-router-dom";
import { GetVariant } from "./Utils/Variant";
import { TradingViewMarque } from "./Components/TradingViewWidgets";
import { useLocalStorage } from "./Hooks";
import style from "./App.module.scss";
import { Pin } from "./Constants/pin";
import "./App.scss";
import { ScrollDepthTracker } from "./Components/ScrollDepthTracker";

const Home = lazy(() => import("./Pages/Home").then((m) => ({ default: m.Home })));
const About = lazy(() => import("./Pages/About").then((m) => ({ default: m.About })));
const Services = lazy(() => import("./Pages/Services").then((m) => ({ default: m.Services })));
const NotFound = lazy(() => import("./Pages/NotFound").then((m) => ({ default: m.NotFound })));
const Media = lazy(() => import("./Pages/Media").then((m) => ({ default: m.Media })));
const Portfolio = lazy(() => import("./Pages/Portfolio").then((m) => ({ default: m.Portfolio })));
const Events = lazy(() => import("./Pages/Events").then((m) => ({ default: m.Events })));
const BusinessSolutions = lazy(() =>
  import("./Pages/BusinessSolutions").then((m) => ({ default: m.BusinessSolutions }))
);
const Pilots = lazy(() => import("./Pages/Pilots").then((m) => ({ default: m.Pilots })));
const RiskProfiler = lazy(() =>
  import("./Pages/RiskProfiler/RiskProfiler").then((m) => ({ default: m.RiskProfiler }))
);
const Calendly = lazy(() =>
  import("./Pages/Calendly/Calendly").then((m) => ({ default: m.Calendly }))
);
const Emc2 = lazy(() => import("./Pages/EMC2").then((m) => ({ default: m.Emc2 })));

const asyncImageLoaders = [
  EagleParallax,
  Plane,
  RetirementPlanning,
  EstatePlanning,
  FinancialPlan,
  ExitStrategy,
  TeamImage,
  TeamImageSecondary,
  TeamImagev3,
  Office,
  RiskIcon,
  SettingsChartIcon,
  ChartUpIcon,
  BarChart,
  Plan,
  MitigatingRisk,
  PlanningPilots,
  StockOptions,
  Consultation,
  WaypointMonitoring,
  CheckList,
  Pilot,
  Business,
  Indivudual,
  Analytics,
  Roadmap,
  Team,
  Vision,
  Chart,
  Shield,
  Product,
  Venue,
  PilotsCockpit,
  BusinessOffice,
  MedicalProfessional,
  RetirementSponsor,
  Timewheel,
  HandShakeImg,
  BusinessStructureImg,
].map(async (src) => {
  let img;
  return new Promise((resolve, reject) => {
    img = new Image();
    img.onload = resolve;
    img.onerror = reject;
    img.src = src;
  });
});

const smallLogoWindowSizes = new Set([
  WindowSizes.smallTablet,
  WindowSizes.tablet,
  WindowSizes.mobile,
  WindowSizes.tinyMobile,
  WindowSizes.smallMobile,
]);

function App(props) {
  const { trackAnalytics, showLockScreen } = GetVariant();
  const [pinVerified] = useLocalStorage("PIN_VERIFIED", false);
  const dispatch = useDispatch();
  const appSize = useSelector((s) => s.utility.appWindowSize);
  const data = useSelector((s) => s.content.metaDataTags.data);

  useEffect(() => {
    // A Google tag ID (GT-...), not a measurement ID: GA4's G-KPX47F22XK is only a
    // destination of this tag, and gtag/js 404s for anything but a tag ID.
    //
    // Page views are left entirely to gtag: config sends the landing one (with its utm_*
    // intact, since any short link was rewritten before render), and the stream's enhanced
    // measurement sends one per client-side navigation. A route hook sending its own as
    // well double-counted every navigation, and its first send ran before this effect had
    // initialised anything, so it was lost.
    const tagId = process.env.REACT_APP_GOOGLE_TAG_ID;
    if (trackAnalytics && tagId) {
      ReactGA.initialize(tagId, {
        gaOptions: { anonymizeIp: true },
        gtagOptions: { anonymize_ip: true },
      });
    }
  }, [trackAnalytics]);

  const updateWindowSize = useCallback(
    (size) => dispatch({ type: UPDATE_WINDOW_SIZE, size }),
    []
  );

  const resizeListener = useCallback(() => {
    if (window.innerWidth <= 450 && appSize !== WindowSizes.tinyMobile)
      updateWindowSize(WindowSizes.tinyMobile);
    else if (window.innerWidth <= 650 && appSize !== WindowSizes.smallMobile)
      updateWindowSize(WindowSizes.smallMobile);
    else if (window.innerWidth <= 875 && appSize !== WindowSizes.mobile)
      updateWindowSize(WindowSizes.mobile);
    else if (window.innerWidth <= 1000 && appSize !== WindowSizes.smallTablet)
      updateWindowSize(WindowSizes.smallTablet);
    else if (window.innerWidth <= 1200 && appSize !== WindowSizes.tablet)
      updateWindowSize(WindowSizes.tablet);
    else if (window.innerWidth <= 1399 && appSize !== WindowSizes.small)
      updateWindowSize(WindowSizes.small);
    else if (window.innerWidth <= 1550 && appSize !== WindowSizes.regular)
      updateWindowSize(WindowSizes.regular);
    else if (window.innerWidth > 1550 && appSize !== WindowSizes.large)
      updateWindowSize(WindowSizes.large);
    else if (window.innerWidth > 2000 && appSize !== WindowSizes.extraLarge)
      updateWindowSize(WindowSizes.extraLarge);
  }, [updateWindowSize]);

  /* Commented out bc it may be the cause of some performance issues */
  useEffect(() => {
    dispatch(GetBusinessServicesAction());
    dispatch(GetIndividualServicesAction());
    dispatch(GetMainMetaTagsAction());
  }, [dispatch]);

  useEffect(() => {
    resizeListener();
    window.onresize = resizeListener;

    const loadImages = async () => {
      await Promise.all(asyncImageLoaders);
    };
    loadImages();
  }, []);

  const paddingTop = useMemo(
    () => (smallLogoWindowSizes.has(appSize) ? 100 : 200),
    [appSize]
  );

  const isLockScreenVisible = useMemo(
    () => !(!showLockScreen || pinVerified),
    [showLockScreen, pinVerified]
  );

  const seoMeta = useMemo(() => data || [], [data]);

  const flexWrapperStyle = useMemo(
    () => ({ width: "100vw", height: "100vh", paddingTop: 0 }),
    [paddingTop]
  );

  return (
    <ParallaxProvider>
      <Router>
        <RouteAwareSEO cmsMeta={seoMeta} />
        <ScrollDepthTracker />
        {isLockScreenVisible && <LockScreen />}
        <Flex column style={flexWrapperStyle}>
          <MobileDrawer />
          <Header />
          <Suspense fallback={<RouteSuspenseFallback />}>
            <Switch>
              <Route exact path="/" component={Home} />
              <Route path="/about" component={About} />
              <Route path="/emc2" component={Emc2} />
              <Route path="/services" component={Services} />
              <Route path="/pilots" component={Pilots} />
              <Route path="/business-solutions" component={BusinessSolutions} />
              <Route path="/resources" component={Media} />
              <Route path="/portfolio" component={Portfolio} />
              <Route path="/events" component={Events} />
              <Route path="/risk-profiler" component={RiskProfiler} />
              <Route path="/calendly" component={Calendly} />
              <Route path="*" component={NotFound} />
            </Switch>
          </Suspense>
          <Footer />
          <AppointmentModal />
          <ContactModal />
          <SuccessAlert />
          <SocialMediaBlock />
          <StaticAppointmentButton />
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              zIndex: 100,
              right: 0,
            }}
          >
            <TradingViewMarque />
          </div>
        </Flex>
      </Router>
    </ParallaxProvider>
  );
}

const RouteAwareSEO = React.memo(({ cmsMeta }) => {
  const { pathname } = useLocation();
  return <SEO path={pathname} meta={cmsMeta} />;
});

// Suspense fallback for lazy-loaded routes. On a phone connection a page's code can take
// seconds to arrive, and an empty fallback leaves the footer drawn directly under the header,
// so this holds the page's shape instead. /events gets its own skeleton to match what follows.
const RouteSuspenseFallback = React.memo(() => {
  const { pathname } = useLocation();
  return pathname.toLowerCase().startsWith("/events") ? <EventsPageSkeleton /> : <PageSkeleton />;
});

const LockScreen = React.memo(() => {
  const [pin, setPin] = useState("");
  const [pinVerified, setPinVerified] = useLocalStorage("PIN_VERIFIED", false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    if (pin?.length === 4 && pin === Pin) setPinVerified(true);
  }, [pin]);

  const handleChange = useCallback(({ target }) => {
    let strippedValue = target.value.split("  ").join("");
    strippedValue = strippedValue.replace(/\D/g, "");
    setPin(strippedValue);
  }, []);

  const isValid = useMemo(() => {
    if (pin?.length === 4 && pin !== Pin) return false;
    return true;
  }, [pin]);

  return (
    <Flex center column className={style.lockScreen}>
      <h1 style={{ textAlign: "center", marginBottom: 15 }}>Welcome</h1>
      <p style={{ textAlign: "center", marginBottom: 30 }}>
        Enter the pin provided to you by an admin.
      </p>
      <InputMask
        onChange={handleChange}
        value={pin}
        style={{ color: "rgba(0,0,0,0.5)" }}
        formatChars={{
          b: "[0-9]",
        }}
        maskChar={"-"}
        mask="b  b  b  b"
      >
        {(inputProps) => (
          <input
            {...inputProps}
            placeholder="-  -  -  -"
            className={style.inputBox}
            autoFocus
          />
        )}
      </InputMask>
      <p style={{ color: "red", marginTop: 10, opacity: isValid ? 0 : 1 }}>
        Invalid pin, please try again or contact an administrator
      </p>
    </Flex>
  );
});

const MaintenanceScreen = React.memo(() => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <Flex center column className={style.lockScreen}>
      <h1 style={{ textAlign: "center", marginBottom: 15 }}>
        Welcome to eagleharboradvisors.com
      </h1>
      <h3 style={{ textAlign: "center", marginBottom: 30 }}>
        Unfortunately our website is undergoing maintenance. We'll be back soon!
      </h3>
    </Flex>
  );
});

export default App;
